import React, { useState, useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import cancelIcon from '../../../shared/assets/svg/cancel_icon.svg';
import toast from 'react-hot-toast';

const AddPaymentTemplate = ({
  handleOnSubmit,
  onClose,
  isSubmitting,
  projectId,
  existingTemplate
}) => {
  const [totalPercentage, setTotalPercentage] = useState(0);

  const { register, handleSubmit, control, setValue, reset } = useForm({
    defaultValues: {
      templateName: '',
      intervals: [{ name: '', percentage: '', duration: '' }]
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'intervals'
  });

  // Use useWatch to properly watch the intervals field
  const watchedIntervals = useWatch({
    control,
    name: 'intervals'
  });

  // Populate form when editing
  useEffect(() => {
    if (existingTemplate) {
      reset({
        templateName: existingTemplate.templateName,
        intervals: existingTemplate.intervals || []
      });
    }
  }, [existingTemplate, reset]);

  // Calculate total percentage
  useEffect(() => {
    if (!watchedIntervals || watchedIntervals.length === 0) {
      setTotalPercentage(0);
      return;
    }
    const total = watchedIntervals.reduce(
      (acc, curr) => acc + (parseFloat(curr?.percentage) || 0),
      0
    );
    setTotalPercentage(total);
  }, [watchedIntervals]);

  const handlePercentageChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue > 100) {
      toast.error('Percentage cannot exceed 100%');
      setValue(`intervals.${index}.percentage`, 100, { shouldValidate: true, shouldDirty: true });
      return;
    }
    setValue(`intervals.${index}.percentage`, numValue, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  // Check if a row is completely empty
  const isRowEmpty = (interval) => {
    const name = (interval?.name || '').toString().trim();
    const percentage = (interval?.percentage || '').toString().trim();
    const duration = (interval?.duration || '').toString().trim();
    return !name && !percentage && !duration;
  };

  // Check if a row is partially filled (has some but not all values)
  const isRowPartiallyFilled = (interval) => {
    const name = (interval?.name || '').toString().trim();
    const percentage = (interval?.percentage || '').toString().trim();
    const duration = (interval?.duration || '').toString().trim();

    const hasName = name.length > 0;
    const hasPercentage = percentage.length > 0;
    const hasDuration = duration.length > 0;

    const filledCount = [hasName, hasPercentage, hasDuration].filter(Boolean).length;

    // If any field is filled but not all are filled, it's partially filled
    return filledCount > 0 && filledCount < 3;
  };

  const handleAddInterval = () => {
    if (totalPercentage >= 100) {
      toast.error("You've reached 100% of Payment");
      return;
    }
    append({ name: '', percentage: '', duration: '' });
  };

  const onSubmit = (data) => {
    // Filter out completely empty rows
    const validIntervals = data.intervals.filter((interval) => !isRowEmpty(interval));

    if (validIntervals.length === 0) {
      toast.error('At least one interval is required');
      return;
    }

    // Check if any row is partially filled
    const partiallyFilledRow = validIntervals.find((interval) => isRowPartiallyFilled(interval));
    if (partiallyFilledRow) {
      toast.error('Please fill all fields in each row');
      return;
    }

    // Recalculate total percentage with only valid intervals
    const newTotalPercentage = validIntervals.reduce(
      (acc, curr) => acc + (parseFloat(curr?.percentage) || 0),
      0
    );

    if (newTotalPercentage !== 100) {
      toast.error('Total percentage must be exactly 100% before saving');
      return;
    }

    const payload = {
      templateName: data.templateName,
      project_id: projectId,
      intervals: validIntervals.map((interval) => ({
        name: interval.name,
        percentage: parseFloat(interval.percentage),
        duration: parseFloat(interval.duration)
      }))
    };

    handleOnSubmit(payload);
  };

  return (
    <div className="mt-10">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Template Name */}
        <div className="w-full">
          <FormInput
            width={100}
            label={'Template Name'}
            register={register}
            register_key={'templateName'}
            type={'text'}
            isRequired={true}
            placeholder="Enter template name"
          />
        </div>

        {/* Intervals Table */}
        <div className="max-h-[30rem] overflow-y-auto hide-scrollbar">
          <table className="w-full table-auto border rounded-lg">
            <thead className="bg-gray-200 !text-gray-600 rounded-t-xl">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Percentage</th>
                <th className="px-4 py-2">Duration (Days)</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields?.map((field, index) => (
                <tr key={field.id}>
                  <td className="px-4 py-2">
                    <FormInput
                      register={register}
                      register_key={`intervals.${index}.name`}
                      type="text"
                      placeholder={`Interval ${index + 1}`}
                    />
                  </td>
                  <td className="px-4 py-2 pt-4">
                    {/* Hidden input to register with react-hook-form */}
                    <input type="hidden" {...register(`intervals.${index}.percentage`)} />
                    <input
                      type="number"
                      placeholder="Percentage"
                      min="0"
                      max="100"
                      step="0.01"
                      onChange={(e) => handlePercentageChange(index, e.target.value)}
                      value={watchedIntervals?.[index]?.percentage || ''}
                      className="border rounded p-2 w-full"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <FormInput
                      register={register}
                      register_key={`intervals.${index}.duration`}
                      type="number"
                      placeholder="Days"
                      min="0"
                    />
                  </td>
                  <td className="px-4">
                    <div className="ml-6" onClick={() => remove(index)}>
                      <img className="cursor-pointer" src={cancelIcon} alt="cancel" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <p>Total Percentage: {totalPercentage.toFixed(2)}%</p>
          {totalPercentage !== 100 && (
            <p className="text-red-500">Total percentage must be exactly 100% before saving</p>
          )}
        </div>

        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            displayType="secondary"
            onClick={handleAddInterval}
            disabled={totalPercentage >= 100}
            type="button">
            Add Interval
          </Button>
          <Button type="submit" disabled={isSubmitting || totalPercentage !== 100}>
            Save Template
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddPaymentTemplate;
